import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PremiumBot, RiskLevel } from '@/types/premium-bots';
import { fetchPremiumBots } from '@/services/premium-bot';
import { useAuth } from '@/contexts/AuthContext';

export const usePremiumBots = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [tagFilter, setTagFilter] = useState<string>('all');
  const userId = user?.id || '';

  // Fetch premium bots from Supabase
  const { 
    data: premiumBots = [],
    isLoading, 
    error, 
    refetch 
  } = useQuery({
    queryKey: ['premiumBots', userId],
    queryFn: () => fetchPremiumBots(userId),
    enabled: !!userId
  });

  // Filter bots based on search query, risk level, and tags
  const filteredBots = premiumBots.filter(bot => {
    // Search query filter
    const matchesSearch = 
      bot.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bot.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Risk level filter
    const matchesRisk = 
      riskFilter === 'all' || 
      (bot.risk && bot.risk.toLowerCase() === riskFilter.toLowerCase());
    
    // Tag filter
    const matchesTag = 
      tagFilter === 'all' || 
      (tagFilter === 'featured' && bot.is_featured) ||
      (tagFilter === 'bestseller' && bot.is_best_seller) ||
      (tagFilter === 'new' && bot.is_new);
    
    return matchesSearch && matchesRisk && matchesTag;
  });
  
  // Helper functions for formatting and styling
  const getRiskLabel = (risk: string | undefined): RiskLevel => {
    if (!risk) return "Medium";
    
    if (risk.toLowerCase() === 'low') return "Low";
    if (risk.toLowerCase() === 'high') return "High";
    
    return "Medium";
  };
  
  const getRiskColor = (risk: string | undefined): string => {
    if (!risk) return "bg-gray-500/20 text-gray-500 border-gray-500/30";
    
    switch (risk.toLowerCase()) {
      case 'low':
        return "bg-green-500/20 text-green-500 border-green-500/30";
      case 'medium':
        return "bg-yellow-500/20 text-yellow-500 border-yellow-500/30";
      case 'high':
        return "bg-red-500/20 text-red-500 border-red-500/30";
      default:
        return "bg-gray-500/20 text-gray-500 border-gray-500/30";
    }
  };

  return {
    premiumBots: filteredBots,
    isLoading,
    error,
    refetch,
    filters: {
      searchQuery,
      setSearchQuery,
      riskFilter,
      setRiskFilter,
      tagFilter,
      setTagFilter
    },
    helpers: {
      getRiskLabel,
      getRiskColor
    }
  };
};
